<script setup lang="ts">
import { ref, inject, computed, Ref, onBeforeMount } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import MultiSelect from 'primevue/multiselect';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const updateData = dialogRef?.value.options.emits.updateData;
const dataEdit = dialogRef?.value.options.data?.data;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const airManagementStore = useAirManagementStore();
const packageManagementStore = usePackageManagementStore();

const rules = {
  airportName: { required },
  airportNameFR: { required },
  airportCode: { required },
  countryName: { required },
  countryNameFr: { required },
  cityName: { required },
  cityNameFr: { required },
  region: { required },
  regionFr: { required }
};

const initData = () => {
  return {
    pk: null,
    sk: null,
    airportCode: '',
    destSVId: '',
    country: '',
    province: '',
    region: '',
    regionFr: '',
    cityCode: '',
    cityName: '',
    cityNameFr: '',
    airportName: '',
    airportNameFR: '',
    isAirCanadaServed: false,
    isAirCanadaVacationsServed: false,
    countryName: '',
    countryNameFr: '',
    gateSts: STATUS_CLOSED,
    destSts: STATUS_CLOSED
  };
};

const airportData = ref(
  dataEdit
    ? cloneDeep({
        ...dataEdit,
        destSVId: dataEdit?.destSVId ? dataEdit?.destSVId?.split(',') : []
      })
    : initData()
);
const v$ = useVuelidate(rules, airportData.value);
const isLoading = ref(false);

const destinations = computed(() => packageManagementStore.destinations);

const listDestSVId = ref([]);

const sortDestinations = (destinationList: any) => {
  const sortDestinationList = destinationList.sort((a: any, b: any) => {
    return a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase());
  });
  return sortDestinationList?.map((destination: any) => {
    return { label: `${destination?.code} - ${destination?.name}`, value: destination?.code };
  });
};

const gateWayStatus = computed({
  get: () => airportData.value.gateSts !== STATUS_CLOSED,
  set: (val) => {
    renderStatusGateway(val);
  }
});

const destinationStatus = computed({
  get: () => airportData.value.destSts !== STATUS_CLOSED,
  set: (val) => {
    renderStatusDestination(val);
  }
});

const renderStatusDestination = (val: any) => {
  airportData.value.destSts = val ? STATUS_OPEN : STATUS_CLOSED;
};

const renderStatusGateway = (val: any) => {
  airportData.value.gateSts = val ? STATUS_OPEN : STATUS_CLOSED;
};

const submit = async () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    const newAirportData = { ...airportData?.value };
    if (newAirportData.destSVId && Array.isArray(newAirportData.destSVId)) {
      newAirportData.destSVId = newAirportData.destSVId.join(',');
    }
    airManagementStore
      .updateAirport(newAirportData)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        updateData();
        dialogRef.value.close();
      })
      .catch((error) => {
        if (error.response && error.response.status === 304) {
          toast.add({ severity: 'error', summary: '', detail: t('notification.airport_has_been_mapped') });
        }
      });
  }
};

onBeforeMount(() => {
  listDestSVId.value = sortDestinations(destinations.value);
});
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="manage-air-modal">
    <Fluid>
      <div class="field grid">
        <div class="col-12 sm:col-6">
          <label for="airportCode">{{ $t('management.air.airport_code') }}</label>
          <InputText data-testid="ip_airport_code" v-model="airportData.airportCode" :class="v$.airportCode.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.airportCode.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-12 sm:col-6">
          <label for="destSVId">{{ $t('management.air.airport_sv_id') }}</label>
          <MultiSelect v-model="airportData.destSVId" :maxSelectedLabels="1" display="chip" :options="listDestSVId" optionLabel="label" filter optionValue="value" :placeholder="t('management.air.select_dest_sv_id')">
            <template #option="slotProps">
              <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis" v-tooltip="{ value: slotProps?.option?.label, disabled: slotProps?.option?.label?.length < 25 }" style="max-width: 200px">
                {{ slotProps.option.label }}
              </div>
            </template>
          </MultiSelect>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="airportName">{{ $t('management.air.airport_name') }}</label>
          <InputText v-model="airportData.airportName" :class="v$.airportName.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.airportName.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="airportNameFR">{{ $t('management.air.airport_name_french') }}</label>
          <InputText v-model="airportData.airportNameFR" :class="v$.airportNameFR.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.airportNameFR.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="grid">
        <div class="col-12 sm:col-6 field">
          <label for="cityName">{{ $t('management.air.city_name') }}</label>
          <InputText v-model="airportData.cityName" :class="v$.cityName.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.cityName.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-12 sm:col-6 field">
          <label for="cityNameFr">{{ $t('management.air.city_name_french') }}</label>
          <InputText v-model="airportData.cityNameFr" :class="v$.cityNameFr.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.cityNameFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="grid">
        <div class="col-12 sm:col-6 field">
          <label for="region">{{ $t('management.air.region') }}</label>
          <InputText v-model="airportData.region" :class="v$.region.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.region.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-12 sm:col-6 field">
          <label for="regionFr">{{ $t('management.air.region_french') }}</label>
          <InputText v-model="airportData.regionFr" :class="v$.regionFr.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.regionFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="grid">
        <div class="col-12 sm:col-6 field">
          <label for="countryName">{{ $t('management.air.country_name') }}</label>
          <InputText v-model="airportData.countryName" :class="v$.countryName.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.countryName.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-12 sm:col-6 field">
          <label for="countryNameFr">{{ $t('management.air.country_name_french') }}</label>
          <InputText v-model="airportData.countryNameFr" :class="v$.countryNameFr.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.countryNameFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="grid">
        <div class="col-12 sm:col-6 field">
          <div class="wrapper">
            <label for="gateSts">{{ $t('management.air.gateway_status') }}</label>
            <ToggleSwitch v-model="gateWayStatus" data-testid="ip_sw_gateway_status" />
          </div>
        </div>
        <div class="col-12 sm:col-6 field">
          <div class="wrapper">
            <label for="destSts">{{ $t('management.air.destination_status') }}</label>
            <ToggleSwitch v-model="destinationStatus" data-testid="ip_sw_destination_status" />
          </div>
        </div>
      </div>
    </Fluid>
    <div class="flex justify-content-center mt-3">
      <Button :label="$t('voucher.submit')" @click="submit()" />
    </div>
  </div>
</template>
